// src/pages/FundPlanner/components/FundStructurer/CashflowSchedule.jsx

import React, { useMemo, useCallback, useRef } from "react";
import ExcelGrid from "components/ExcelGrid";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { Plus, Trash2 } from "lucide-react";
import dayjs from "lib/dayjs";
import { makeNiceScaleZeroBased } from "lib/chartScale";

import {
  coerceDateCell,
  coerceNumberCell,
} from "utils/pasteExpand";

/* ---------- Helpers ---------- */
const formatKMB = (v) => {
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return (v || 0).toLocaleString();
};

const formatFull2dp = (v, s = "$") =>
  `${s}${(Number(v) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function isBlankRow(r) {
  const d = (r?.drawDate ?? "").toString().trim();
  const a = r?.drawRequirement;
  const aBlank =
    a == null || (typeof a === "string" && a.trim() === "") || a === "";
  return !d && aBlank;
}

function num(x, dflt = null) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

function getCashflowAmount(row) {
  if (!row) return null;

  if (typeof row.drawRequirement === "number") {
    return Number.isFinite(row.drawRequirement)
      ? row.drawRequirement
      : null;
  }

  return coerceNumberCell(row.drawRequirement);
}

function getCashflowTotal(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const amount = getCashflowAmount(row);
    return amount == null ? sum : sum + amount;
  }, 0);
}

/**
 * Semantic equality for schedules:
 * - ignore `id` churn (ExcelGrid may regen ids)
 * - treat numeric strings and numbers as equivalent
 * - normalize blanks
 */
function normAmt(v) {
  if (v == null) return "";

  if (typeof v === "number") {
    return Number.isFinite(v) ? String(v) : "";
  }

  const s = String(v).trim();

  if (!s) return "";

  const cleaned = s.replace(/,/g, "");
  const n = Number(cleaned);

  return Number.isFinite(n) ? String(n) : s;
}

function normDate(v) {
  const s = String(v ?? "").trim();
  return s;
}

function ABlankRow(rows) {
  return Array.isArray(rows) && rows.length
    ? rows[0]
    : { drawDate: "", drawRequirement: "" };
}

function schedulesEquivalent(a, b) {
  const A = Array.isArray(a) ? a : [];
  const B = Array.isArray(b) ? b : [];

  const ABlank =
    A.length === 0 || (A.length === 1 && isBlankRow(ABlankRow(A)));

  const BBlank =
    B.length === 0 || (B.length === 1 && isBlankRow(ABlankRow(B)));

  if (ABlank && BBlank) return true;

  if (A.length !== B.length) return false;

  for (let i = 0; i < A.length; i++) {
    const ad = normDate(A[i]?.drawDate);
    const bd = normDate(B[i]?.drawDate);

    if (ad !== bd) return false;

    const aa = normAmt(A[i]?.drawRequirement);
    const ba = normAmt(B[i]?.drawRequirement);

    if (aa !== ba) return false;
  }

  return true;
}

/* ---------- Component ---------- */
export default function DrawSchedule({
  drawRows,
  setDrawRows,
  isSolving,
  onRunWizard,

  fundType = "project",
  totalAmount = null,
  setTotalAmount,
  isReadOnly = false,
}) {
  const fmt2 = (v) =>
    v != null && v !== ""
      ? Number(v).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "";
  const emptyRowRef = useRef({
    id: "blank-requirement-" + Date.now(),
    drawDate: "",
    drawRequirement: "",
  });
  const gridRows =
    !isReadOnly && (!Array.isArray(drawRows) || drawRows.length === 0)
      ? [emptyRowRef.current]
      : drawRows;

  const isRefunding =
    String(fundType || "").toLowerCase() === "refunding";

  const gross = num(totalAmount, 0) || 0;

  const cashflowTotal = useMemo(
    () => getCashflowTotal(drawRows),
    [drawRows]
  );

  /* ------------------------------------------------
   * Sync helper
   * ------------------------------------------------ */
  const syncTotalAmountFromRows = useCallback(
    (rows) => {
      if (typeof setTotalAmount !== "function") return;

      const nextTotal = getCashflowTotal(rows);
      setTotalAmount(nextTotal);
    },
    [setTotalAmount]
  );

  /* ------------------------------------------------
   * Seed schedule (only when empty/effectively blank)
   * ------------------------------------------------ */
  React.useEffect(() => {
    if (isReadOnly) return;
    const rows = Array.isArray(drawRows) ? drawRows : [];

    const empty = rows.length === 0;
    const effectivelyBlank =
      rows.length === 1 && isBlankRow(rows[0]);

    if (!empty && !effectivelyBlank) return;

    if (isRefunding) {
      setDrawRows([
        {
          id: Date.now(),
          drawDate: "",
          drawRequirement: gross > 0 ? gross : "",
        },
      ]);

      return;
    }

    setDrawRows([
      {
        id: Date.now(),
        drawDate: "",
        drawRequirement: "",
      },
    ]);
  }, [drawRows, setDrawRows, isRefunding, gross, isReadOnly]);

  /* ---------- Guarded grid onChange ---------- */
  const handleGridChange = useCallback(
    (nextRows) => {
      const normalizedRows = Array.isArray(nextRows)
        ? nextRows
        : [];

      setDrawRows((prev) => {
        if (schedulesEquivalent(prev, normalizedRows)) {
          return prev;
        }

        return normalizedRows;
      });

      syncTotalAmountFromRows(normalizedRows);
    },
    [setDrawRows, syncTotalAmountFromRows]
  );

  /* ---------- Row handlers ---------- */
  const handleAddBelow = (row) => {
    const rows = Array.isArray(drawRows) ? drawRows : [];

    const index = rows.findIndex((r) => r.id === row.id);

    const newRow = {
      id: Date.now(),
      drawDate: dayjs(row.drawDate || dayjs())
        .add(1, "day")
        .format("YYYY-MM-DD"),
      drawRequirement: "",
    };

    const updated = [...rows];

    updated.splice(index + 1, 0, newRow);

    setDrawRows(updated);
    syncTotalAmountFromRows(updated);
  };

  const handleDeleteRow = (id) => {
    const rows = Array.isArray(drawRows) ? drawRows : [];

    const updated = rows.filter((r) => r.id !== id);

    if (!updated.length) {
      if (isRefunding) {
        const seeded = [
          {
            id: Date.now(),
            drawDate: "",
            drawRequirement: gross > 0 ? gross : "",
          },
        ];

        setDrawRows(seeded);
        syncTotalAmountFromRows(seeded);

        return;
      }

      const blank = [
        {
          id: Date.now(),
          drawDate: "",
          drawRequirement: "",
        },
      ];

      setDrawRows(blank);
      syncTotalAmountFromRows(blank);

      return;
    }

    setDrawRows(updated);
    syncTotalAmountFromRows(updated);
  };

  /* ---------- Table Columns ---------- */
  const columns = [
    {
      key: "drawDate",
      label: "Date",
      editable: !isReadOnly,
      date: true,
      type: "date",
      editor: "date",
      className: "numeric",
    },
    {
      key: "drawRequirement",
      label: "Requirement ($)",
      editable: !isReadOnly,
      numeric: true,
      className: "text-right numeric",
      headerClassName: "text-right",
      format: fmt2,
    },
    {
      key: "actions",
      label: "Add / Delete Row",
      width: 110,
      headerClassName: "text-left",
      render: (row) => isReadOnly ? null : (
        <div className="flex items-center h-6 justify-start gap-1 pl-1">
          <button
            type="button"
            onClick={() => handleAddBelow(row)}
            title="Add row below"
            className="text-green-600 hover:text-green-800"
          >
            <Plus size={16} />
          </button>

          <button
            type="button"
            onClick={() => handleDeleteRow(row.id)}
            title="Delete row"
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  /* ---------- Chart Data ---------- */
  const chartData = useMemo(() => {
    return (drawRows || [])
      .map((r) => {
        const date = coerceDateCell(r.drawDate);
        const amt = getCashflowAmount(r);

        if (!date || amt == null) return null;

        return {
          date,
          amount: amt,
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [drawRows]);

  const scale = useMemo(() => {
    const maxVal = Math.max(
      ...chartData.map((d) => d.amount || 0),
      0
    );

    return makeNiceScaleZeroBased(maxVal, 6, 1);
  }, [chartData]);

  /* ---------- Render ---------- */
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1">
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <div className="text-xs text-muted tracking-wide">
              Total Cash Flows
            </div>

            <div className="mt-1 text-xl font-semibold text-app">
              {formatFull2dp(cashflowTotal, "$")}
            </div>
          </div>
        </div>

        {!isRefunding && (
          <button
            type="button"
            onClick={
              typeof onRunWizard === "function"
                ? onRunWizard
                : undefined
            }
            disabled={isReadOnly || typeof onRunWizard !== "function"}
            className="btn btn-primary mb-1"
            title={
              typeof onRunWizard === "function"
                ? "Open Cash Flow Wizard"
                : "Wizard action not wired"
            }
          >
            Cash Flow Wizard
          </button>
        )}
      </div>

      {chartData.length > 0 ? (
        <div className="h-80 bg-card border border-border rounded-xl p-4 shadow-sm">
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="date" />

              <YAxis
                domain={[scale.niceMin, scale.niceMax]}
                ticks={scale.ticks}
                tickFormatter={(v) => `$${formatKMB(v)}`}
              />

              <Tooltip
                formatter={(val) =>
                  formatFull2dp(val, "$")
                }
                labelFormatter={(d) => `Date: ${d}`}
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border:
                    "1px solid hsl(var(--border))",
                  color: "hsl(var(--foreground))",
                }}
              />

              <Bar
                dataKey="amount"
                fill="var(--chart-1)"
                name="Amount"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="p-4 text-muted border border-border rounded-xl">
          No requirements data available.
        </div>
      )}

      <div className="max-w-[700px] text-sm leading-tight">
        <ExcelGrid
          columns={columns}
          rows={gridRows}
          onChange={isReadOnly ? undefined : handleGridChange}
          allowPasteAppendRows={true}
          makePasteRow={(partial = {}, i = 0) => ({
            id: Date.now() + i,
            drawDate: partial.drawDate ?? "",
            drawRequirement:
              partial.drawRequirement != null
                ? partial.drawRequirement
                : "",
          })}
        />
      </div>

      {isSolving ? (
        <div className="text-muted text-xs">
          Solving…
        </div>
      ) : null}
    </div>
  );
}
